<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Post Payment" hide-footer>
        <!-- The charge stands but no payment row will appear, so left to a toast staff would read the
             unchanged ledger as "it did not take" and charge again. Stays until the modal is closed. -->
        <b-alert :show="!!unrecordedCharge" variant="danger" class="py-2">
            The card was charged <strong>{{ currency(unrecordedCharge && unrecordedCharge.amount) }}</strong>
            (Stripe reference <strong class="text-monospace">{{ unrecordedCharge && unrecordedCharge.reference }}</strong>) but the payment
            could not be recorded. <strong>Do not charge the card again.</strong> Contact support to have this
            payment posted manually.
        </b-alert>

        <b-row>
            <b-col cols="6">
                <b-form-group label="Amount">
                    <b-form-input v-model.number="form.amount" type="number" min="0" step="0.01" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group label="Method">
                    <b-select v-model.number="form.paymentMethod" :options="paymentMethodOptions" />
                </b-form-group>
            </b-col>
        </b-row>

        <b-form-group v-if="form.paymentMethod === 2" label="Check #">
            <b-form-input v-model="form.checkNumber" />
        </b-form-group>

        <template v-else-if="form.paymentMethod === 3">
            <!-- Charging is only offered when the pharmacy has card payments enabled; without it the
                 reference remains the only option, which is how AR card payments behaved before. -->
            <b-form-group v-if="cardPaymentsEnabled">
                <b-form-radio-group v-model="form.cardEntryMode" stacked>
                    <b-form-radio value="saved">Charge the patient's saved card</b-form-radio>
                    <b-form-radio value="reference">Card processed outside the system</b-form-radio>
                </b-form-radio-group>
            </b-form-group>

            <template v-if="useSavedCard">
                <div v-if="loadingCards" class="mb-2">
                    <b-spinner small /> Loading saved cards...
                </div>
                <template v-else>
                    <b-form-group label="Card to charge">
                        <b-select v-model="form.stripeCardId" :options="cardOptions" />
                        <small v-if="!cards.length" class="text-muted">
                            The responsible patient has no saved cards on file — choose New card to add one.
                        </small>
                    </b-form-group>

                    <!-- Entered here rather than on the Payment Methods modal so the amount and notes
                         already typed survive. The card attaches to the account's responsible party —
                         the server resolves who that is — and is selected as soon as it saves. -->
                    <b-form-group v-if="addingCard" label="New card">
                        <StripeVueCard :post-url="addCardUrl" @cardAdded="onCardAdded" />
                        <b-button size="sm" variant="link" class="px-0" @click="form.stripeCardId = null">
                            Cancel
                        </b-button>
                    </b-form-group>
                </template>
            </template>

            <b-form-group v-else label="Reference">
                <b-form-input v-model="form.referenceId" placeholder="Receipt or authorization number" />
                <small class="text-muted">Recorded for reconciliation. No card is charged.</small>
            </b-form-group>
        </template>

        <b-form-group label="Notes">
            <b-form-input v-model="form.notes" />
        </b-form-group>

        <div class="text-right">
            <b-button variant="secondary" class="mr-2" :disabled="saving" @click="close">
                {{ unrecordedCharge ? "Close" : "Cancel" }}
            </b-button>
            <b-button v-if="!unrecordedCharge" variant="primary" :disabled="saving || !canPost" @click="postPayment">
                <b-spinner v-if="saving" small class="mr-1" />
                {{ willChargeCard ? "Charge Card" : "Post Payment" }}
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ARPaymentMethodOptions, ARPaymentView, ARSavedCard, arSavedCardOptions } from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { generateRequestId, shouldResetTokenOnError } from "@/util/orderIdempotency";
import StripeVueCard from "@/components/Payments/StripeVueCardcomponent.vue";

// Picked from the card list rather than offered as a separate button: which card to charge and
// adding the card to charge are the same decision, so they belong in one control. It is a sentinel,
// never a card id, so it must not reach the button label, canPost, or the payload.
const NEW_CARD = "__new_card__";

@Component({
    name: "ARPostPaymentModal",
    components: {
        StripeVueCard,
    },
})
export default class ARPostPaymentModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;
    // Provided by the parent card, which already loaded the configuration to decide which AR
    // actions to offer — passing it down avoids re-fetching a flag that would then diverge.
    @Prop({ type: Boolean, default: false }) cardPaymentsEnabled!: boolean;

    private saving = false;
    private loadingCards = false;
    private cardsLoaded = false;
    private cards: ARSavedCard[] = [];
    private paymentMethodOptions = ARPaymentMethodOptions;
    // Sent with a card charge so a resubmit after a timeout, or a double-click, collapses into a
    // single Stripe charge rather than billing the patient twice.
    private idempotencyKey = generateRequestId();
    private form = this.emptyForm();
    // Set when the server answers 202: the card was charged but the payment was not recorded. This
    // is terminal for the open modal — nothing more can be posted from it — because the alternative,
    // closing on a success toast, leaves staff looking at an unchanged ledger and charging again.
    private unrecordedCharge: { amount: number; reference: string | null } | null = null;

    @Watch("value")
    onShow(visible: boolean): void {
        if (!visible) return;
        this.form = this.emptyForm();
        this.cards = [];
        this.cardsLoaded = false;
        this.unrecordedCharge = null;
        this.idempotencyKey = generateRequestId();
    }

    // Saved cards cost a Stripe round-trip, so they are fetched the first time a card charge is
    // actually selected rather than on every payment — most payments are cash or check.
    @Watch("useSavedCard")
    async onUseSavedCardChanged(useSavedCard: boolean): Promise<void> {
        if (useSavedCard) await this.loadCards();
    }

    private emptyForm() {
        return {
            amount: 0,
            paymentMethod: 1,
            checkNumber: "",
            referenceId: "",
            notes: "",
            // Charging is the default when it is available; otherwise the reference is the only route.
            cardEntryMode: this.cardPaymentsEnabled ? "saved" : "reference",
            stripeCardId: null as string | null,
        };
    }

    get useSavedCard(): boolean {
        return this.form.paymentMethod === 3
            && this.cardPaymentsEnabled
            && this.form.cardEntryMode === "saved";
    }

    // The selection once the new-card sentinel is filtered out — the only value that names a card
    // the charge could actually use.
    get chargeableCardId(): string | null {
        const id = this.form.stripeCardId;
        return id && id !== NEW_CARD ? id : null;
    }

    // Choosing New card in the picker is what opens the entry form; there is no separate toggle to
    // fall out of step with the selection.
    get addingCard(): boolean {
        return this.useSavedCard && this.form.stripeCardId === NEW_CARD;
    }

    get willChargeCard(): boolean {
        return this.useSavedCard && !!this.chargeableCardId;
    }

    get cardOptions(): { value: string | null; text: string }[] {
        return [...arSavedCardOptions(this.cards), { value: NEW_CARD, text: "New card..." }];
    }

    // A card payment has to name how it settles: the card being charged, or the reference for a card
    // charged elsewhere. Neither would post a payment that records nothing about how it was taken.
    get canPost(): boolean {
        if (this.unrecordedCharge) return false;
        if (!(this.form.amount > 0)) return false;
        if (this.form.paymentMethod !== 3) return true;
        return this.useSavedCard ? !!this.chargeableCardId : !!this.form.referenceId?.trim();
    }

    private async loadCards(): Promise<void> {
        if (!this.accountId || this.cardsLoaded) return;
        try {
            this.loadingCards = true;
            const response = await axios.get<ARSavedCard[]>(`/AR/accounts/${this.accountId}/payment-methods`);
            this.cards = response.data ?? [];
            this.cardsLoaded = true;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loadingCards = false;
        }
    }

    // Check number, reference and card each belong to a single settlement route, so only the one the
    // chosen route uses is sent; the others would otherwise carry over from a prior selection.
    buildPayload() {
        const isCard = this.form.paymentMethod === 3;
        // The same getter the template and canPost read, so what is sent cannot disagree with the
        // route the form is showing.
        const chargingSavedCard = this.useSavedCard;
        return {
            amount: this.form.amount,
            paymentMethod: this.form.paymentMethod,
            checkNumber: this.form.paymentMethod === 2 ? this.form.checkNumber : null,
            referenceId: isCard && !chargingSavedCard ? this.form.referenceId : null,
            stripeCardId: chargingSavedCard ? this.chargeableCardId : null,
            idempotencyKey: chargingSavedCard ? this.idempotencyKey : null,
            notes: this.form.notes,
        };
    }

    async postPayment(): Promise<void> {
        if (!this.accountId) return;
        const charged = this.willChargeCard;
        try {
            this.saving = true;
            const response = await axios.post<ARPaymentView>(`/AR/accounts/${this.accountId}/payments`, this.buildPayload());
            // 202 is the server's "the money moved but the ledger did not": hold the modal open on
            // the charge rather than closing on a success it cannot honestly report.
            if (response.status === 202) {
                this.unrecordedCharge = { amount: this.form.amount, reference: response.data?.stripeChargeId ?? null };
                return;
            }
            this.$notification(NotificationOptions.success(charged ? "Card charged and payment posted." : "Payment posted."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            // A rejected charge cannot be retried under the same key — Stripe would replay the
            // decline even if the user picks a different card — so mint a fresh one. A network or
            // server failure keeps the key so a retry collapses instead of charging twice.
            if (shouldResetTokenOnError((error as { response?: { status?: number } })?.response?.status)) {
                this.idempotencyKey = generateRequestId();
            }
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    // The account decides which customer a card belongs to, so the token goes to the AR endpoint
    // rather than the patient-scoped one: on a shared account the responsible party is not always
    // the patient whose dashboard is open, and a card on the wrong customer could never be charged.
    get addCardUrl(): string | null {
        return this.accountId ? `/AR/accounts/${this.accountId}/payment-methods` : null;
    }

    // Reload rather than pushing the new card straight into the list. The picker is built from the
    // payment-methods endpoint, and only a card that endpoint returns is one the charge can use — so
    // the card is selected only once it comes back from there.
    async onCardAdded(card: ARSavedCard): Promise<void> {
        this.cardsLoaded = false;
        await this.loadCards();
        // Selecting it also closes the entry form, since the form is shown by the sentinel still
        // being selected. A card the list does not return leaves both open rather than arming a
        // charge against something the picker cannot show.
        if (card?.id && this.cards.some(c => c.id === card.id)) this.form.stripeCardId = card.id;
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
